import React, { useEffect, useRef, useState } from 'react';
import './IndexChartWidget.css';
import '../../Components/Common/typography.css';

import {
  ChartTypeKey,
  IntervalKey,
  PeriodKey,
} from '../AdvanceChartWidget/types';
import {
  DEFAULT_ACTIVE_TA,
  DEFAULT_CANDLESTICK_COLORS,
  DEFAULT_LINE_CONFIG,
  DEFAULT_VIEW,
} from '../AdvanceChartWidget/constants';
import ChartView from '../AdvanceChartWidget/components/ChartView';

import Layout from '../../Components/Common/Layout/Layout';
import useContainerWidth from '../../hooks/useContainerWidth';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';

import { ReactComponent as ChartTypeButtonIcon } from '../../assets/icons/StockChart/calistic-icon.svg';
import { ReactComponent as CandlestickOptionIcon } from '../../assets/icons/StockChart/dark-calistic.svg';
import { ReactComponent as LineOptionIcon } from '../../assets/icons/StockChart/Union.svg';
import { ReactComponent as BarOptionIcon } from '../../assets/icons/StockChart/Bars.svg';

export interface IndexChartWidgetProps extends BaseWidgetProps {
  symbol?: string;
  token?: string;
  chartBundleUrl?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

type IndexPeriodKey = '1D' | '5D' | '1M' | '3M' | '6M' | '1Y' | '5Y';

const INDEX_PERIOD_LIST: { key: IndexPeriodKey; label: string }[] = [
  { key: '1D', label: '1D' },
  { key: '5D', label: '5D' },
  { key: '1M', label: '1M' },
  { key: '3M', label: '3M' },
  { key: '6M', label: '6M' },
  { key: '1Y', label: '1Y' },
  { key: '5Y', label: '5Y' },
];

const CHART_TYPE_OPTIONS: {
  key: ChartTypeKey;
  label: string;
  icon: React.ReactNode;
}[] = [
  { key: 'candlestick', label: 'Candlestick', icon: <CandlestickOptionIcon /> },
  { key: 'line', label: 'Line', icon: <LineOptionIcon /> },
  { key: 'bar', label: 'Bar', icon: <BarOptionIcon /> },
];

function getIntervalForPeriod(period: IndexPeriodKey): IntervalKey {
  if (period === '1D') return '5';
  if (period === '5D') return '60';
  if (period === '6M' || period === '1Y') return 'w';
  if (period === '5Y') return 'm';
  return 'd';
}

const IndexChartWidget: React.FC<IndexChartWidgetProps> = (props) => {
  const {
    theme = ThemeType.Direct,
    token = '',
    symbol = '',
    onHeightChange,
  } = props;

  const [selectedPeriod, setSelectedPeriod] = useState<IndexPeriodKey>('3M');
  const [selectedChartType, setSelectedChartType] =
    useState<ChartTypeKey>('candlestick');
  const [isChartTypePopupOpen, setIsChartTypePopupOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const chartTypeButtonWrapperRef = useRef<HTMLDivElement>(null);

  useResizeNotifier(containerRef, WidgetNameSource.IndexChart, onHeightChange);
  const { isSmallScreen: isMobile } = useContainerWidth(containerRef);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        chartTypeButtonWrapperRef.current &&
        !chartTypeButtonWrapperRef.current.contains(e.target as Node)
      ) {
        setIsChartTypePopupOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const interval = getIntervalForPeriod(selectedPeriod);

  return (
    <Layout theme={theme}>
      <div className="index-chart-widget-container" ref={containerRef}>
        {/* ── Header: period tabs + chart type selector ── */}
        <div
          className={`index-chart-header${isMobile ? ' index-chart-header-mobile' : ''}`}
        >
          <div className="index-chart-period-tab-bar">
            {INDEX_PERIOD_LIST.map(({ key, label }) => (
              <button
                key={key}
                className={`index-chart-period-tab body-m-bold${
                  selectedPeriod === key ? ' index-chart-period-tab-active' : ''
                }`}
                onClick={() => setSelectedPeriod(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div
            className="index-chart-chart-type-button-wrapper"
            ref={chartTypeButtonWrapperRef}
          >
            <button
              className={`index-chart-chart-type-button${
                isChartTypePopupOpen
                  ? ' index-chart-chart-type-button-open'
                  : ''
              }`}
              onClick={() => setIsChartTypePopupOpen((v) => !v)}
              title="Chart type"
            >
              <ChartTypeButtonIcon />
            </button>

            {isChartTypePopupOpen && (
              <div
                className="index-chart-chart-type-popup"
                data-outside-ignore="true"
              >
                {CHART_TYPE_OPTIONS.map(({ key, label, icon }) => (
                  <div
                    key={key}
                    className={`index-chart-chart-type-option body-m-regular${
                      selectedChartType === key
                        ? ' index-chart-chart-type-option-active'
                        : ''
                    }`}
                    onClick={() => {
                      setSelectedChartType(key);
                      setIsChartTypePopupOpen(false);
                    }}
                  >
                    <span className="index-chart-chart-type-option-icon">
                      {icon}
                    </span>
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Chart body ── */}
        <div className="index-chart-body">
          {symbol ? (
            <ChartView
              symbol={symbol}
              token={token}
              interval={interval}
              period={selectedPeriod as PeriodKey}
              chartType={selectedChartType}
              candlestickColors={DEFAULT_CANDLESTICK_COLORS}
              lineConfig={DEFAULT_LINE_CONFIG}
              activeTA={DEFAULT_ACTIVE_TA}
              comparisonList={[]}
              comparisonMode={false}
              view={{ ...DEFAULT_VIEW, showTimelineScroller: false }}
              chartBundleUrl={props.chartBundleUrl}
              onLoadingChange={props.onLoadingChange}
              onNoData={() =>
                props.onNoData?.({
                  type: WidgetMessageType.NoData,
                  source: WidgetNameSource.IndexChart,
                  data: null,
                })
              }
              onError={(err) => props.onError?.(err)}
            />
          ) : (
            <ErrorMessage msg={errorMsg.find((m) => m.key === 'chart')} />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default IndexChartWidget;
